import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ClipLoader from 'react-spinners/ClipLoader';
import { isMobile } from 'react-device-detect';

import './styles/globals.css';
import loadingStyles from './styles/Loading.module.css';
import errorStyles from './styles/Error.module.css';
import statusStyles from './styles/StatusView.module.css';
import { useSmartSendQuery } from './hooks/useSmartSendQuery';
import HeroVideo from './components/HeroVideo';
import SmartSendFlow from './components/SmartSendFlow';
import AppBar from './components/AppBar';
import ErrorBoundary from './components/ErrorBoundary';
import { getFirstName } from './utils/formHelpers';
import { SmartSendProvider } from './contexts/SmartSendContext';
import {
  saveVerificationCode,
  cleanupExpiredItems,
  getPhoneNumber,
} from './utils/localStorage';
import { HERO_VIDEO_URL, GESTURE_DOWNLOAD_URL, isDevelopment, ANALYTICS_SOURCE } from './env/config';
import analytics from '../../library/segmentAnalytics';

/**
 * SmartSendV2 - Main entry point for SmartSend v2 application
 * Modern rewrite of SmartSend with improved architecture and UX
 *
 * Handles view state routing based on order status:
 * - LOADING: Initial data fetch
 * - ERROR: API or validation errors
 * - NO_DATA: Missing order information
 * - COMPLETED: Order already submitted
 * - CANCELLED: Order was cancelled
 * - EXPIRED: Product SKU expired
 * - MAIN_FORM: Active order - main delivery flow
 *
 * Uses React Context for state management and modern functional components
 */
const SmartSendV2 = () => {

  // ============================================
  // PARSE URL PARAMETERS
  // ============================================
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('orderId');
  // tokenId is provided by consumer app via URL params when opening webview (optional - null for web G-links)
  // Used only in Authorization header as Bearer token, not in backend URLs or request body
  // Token validation is handled by the backend
  const tokenId = params.get('tokenId');
  const inApp = params.get('app') === 'true';
  const code = params.get('code'); // Verification code for seamless deep linking
  const skipIntro = params.get('skipIntro') === 'true';

  // ============================================
  // LOCAL STATE
  // ============================================
  const [showContent, setShowContent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState(null);
  const [skipVideo, setSkipVideo] = useState(false);
  // Track which orderId has been tracked to allow tracking when orderId changes
  const trackedOrderId = useRef(null);

  // ============================================
  // VIEWPORT LOCK - Apply on mount, remove on unmount
  // ============================================
  // These styles prevent scroll/bounce on mobile devices but must be
  // scoped to SmartSend only to avoid affecting other pages (GFeed, PM, etc.)
  useEffect(() => {
    // Store original styles to restore on unmount
    const originalHtmlOverflow = document.documentElement.style.overflow || '';
    const originalHtmlOverscroll =
      document.documentElement.style.overscrollBehavior || '';
    const originalHtmlTouchAction =
      document.documentElement.style.touchAction || '';
    const originalBodyOverflow = document.body.style.overflow || '';
    const originalBodyOverscroll = document.body.style.overscrollBehavior || '';
    const originalBodyTouchAction = document.body.style.touchAction || '';
    const root = document.getElementById('root');
    const originalRootOverflow = root?.style.overflow || '';

    // Apply viewport lock styles
    // Matches original CSS: html, body { overflow: hidden; overscroll-behavior: none; touch-action: pan-y pinch-zoom; }
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.documentElement.style.touchAction = 'pan-y pinch-zoom';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.touchAction = 'pan-y pinch-zoom';
    if (root) {
      root.style.overflow = 'hidden';
    }

    // Cleanup: Restore original styles when SmartSend unmounts
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.overscrollBehavior =
        originalHtmlOverscroll;
      document.documentElement.style.touchAction = originalHtmlTouchAction;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.overscrollBehavior = originalBodyOverscroll;
      document.body.style.touchAction = originalBodyTouchAction;
      if (root) {
        root.style.overflow = originalRootOverflow;
      }
    };
  }, []);

  // ============================================
  // INITIALIZATION ON MOUNT
  // ============================================
  useEffect(() => {
    // Clean up expired localStorage items
    cleanupExpiredItems();

    if (!orderId) {
      return;
    }

    // If code is present in URL, save it to localStorage for later use
    if (code) {
      saveVerificationCode(orderId, code);
    }

    // Determine if video should be skipped:
    // - If skipIntro=true is explicitly set, always skip
    // - If in app mode (app=true), only skip if skipIntro=true (don't check localStorage)
    // - If in browser mode, skip if user has already started verification (hasStarted check)
    const hasStarted = getPhoneNumber(orderId) !== null;
    if (skipIntro || (!inApp && hasStarted)) {
      setSkipVideo(true);
      setShowContent(true);
    }
  }, [code, orderId, skipIntro, inApp]);

  // ============================================
  // FETCH ORDER DATA
  // ============================================
  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
  } = useSmartSendQuery(orderId, tokenId);

  // Extract order data and secure header from API response
  const orderData = apiResponse?.result;
  const secureVerifyHeader = apiResponse?.secureVerifyHeader;

  // Debug: Log query state for troubleshooting
  useEffect(() => {
    const queryDebugInfo = {
      isLoading,
      isError,
      hasError: !!error,
      errorMessage: error?.message,
      hasData: !!apiResponse,
      hasOrderData: !!orderData,
      orderId,
      hasToken: !!tokenId,
      pmId: orderData?.pmId,
      pmIdType: typeof orderData?.pmId,
    };
    if (isDevelopment()) {
      console.log('[SmartSend] Query state:', queryDebugInfo);
    }
  }, [isLoading, isError, error, apiResponse, orderData, orderId, tokenId]);

  // ============================================
  // DETERMINE VIEW STATE
  // ============================================
  const getViewState = () => {
    if (isLoading) return 'LOADING';
    if (isError) {
      // Check if error is due to expired SKU
      if (error?.message === 'SKU_EXPIRED') return 'EXPIRED';
      return 'ERROR';
    }
    if (!orderData) return 'NO_DATA';

    // Check if form was just submitted locally
    if (isSubmitted) return 'COMPLETED';

    // Determine view based on actual API response fields
    if (orderData.cancelled) return 'CANCELLED';
    if (orderData.submitted_by_recipient === true) return 'COMPLETED';

    return 'MAIN_FORM';
  };

  const viewState = getViewState();

  // ============================================
  // ANALYTICS: TRACK FORM LOADED
  // ============================================
  useEffect(() => {
    // Reset form state when orderId changes to ensure clean state for new order
    // This must run first to ensure trackedOrderId is reset before tracking logic
    if (
      orderId &&
      typeof orderId === 'string' &&
      orderId.trim() &&
      trackedOrderId.current !== null &&
      trackedOrderId.current !== orderId
    ) {
      // Reset form state for new order
      setIsSubmitted(false);
      setSubmittedOrderId(null);
      setShowContent(false);
      setSkipVideo(false);
      // Reset tracked order ID to allow tracking of new orderId
      trackedOrderId.current = null;
    }

    // Track when form loads (MAIN_FORM state with orderData)
    // Only track if this is a different orderId than previously tracked
    if (
      viewState === 'MAIN_FORM' &&
      orderData &&
      orderId &&
      typeof orderId === 'string' &&
      orderId.trim() &&
      trackedOrderId.current !== orderId
    ) {
      // Remember this orderId to prevent duplicate tracking for the same order
      trackedOrderId.current = orderId;

      // Determine device type
      const deviceType = isMobile ? 'mobile' : 'desktop';

      // Build properties with all available context variables
      const properties = {
        source: ANALYTICS_SOURCE,
        type: deviceType,
        orderId,
        // Include all orderData fields
        ...(orderData && {
          // Order status fields
          cancelled: orderData.cancelled,
          submitted_by_recipient: orderData.submitted_by_recipient,
          // Recipient info
          recipientName: orderData.recipientName,
          recipientPhone: orderData.recipientPhone,
          recipientEmail: orderData.recipientEmail,
          // Sender info
          senderName: orderData.senderName,
          senderPhone: orderData.senderPhone,
          senderEmail: orderData.senderEmail,
          // Product info
          productId: orderData.productId,
          productName: orderData.productName,
          skuId: orderData.skuId,
          // Delivery info
          deliveryMethod: orderData.deliveryMethod,
          // App mode
          inApp: inApp || false,
          // URL params
          tokenId: tokenId || null,
          skipIntro: skipIntro || false,
          hasCode: !!code,
        }),
      };

      analytics.track('SmartSend Form Loaded', properties);
    }
  }, [viewState, orderData, orderId, inApp, tokenId, skipIntro, code]);

  // ============================================
  // ERROR MESSAGE HELPER
  // ============================================
  const getErrorMessage = () => {
    if (!error?.message)
      return 'An unexpected error occurred. Please try again.';

    // Handle rate limiting specifically
    if (error.message.includes('429')) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    // Handle other specific errors
    if (error.message.includes('Network')) {
      return 'Network error. Please check your connection and try again.';
    }

    return error.message;
  };

  // ============================================
  // RENDER
  // ============================================
  switch (viewState) {
    case 'LOADING':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={loadingStyles.loader}>
            <ClipLoader color="#ffffff" size={50} />
            <p className={loadingStyles.loadingText}>Loading your Gesture...</p>
          </div>
        </>
      );

    case 'ERROR':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={errorStyles.errorContainer}>
            <div className={errorStyles.errorCard}>
              <h2 className={errorStyles.errorTitle}>Something went wrong</h2>
              <p className={errorStyles.errorMessage}>{getErrorMessage()}</p>
              {!inApp && (
                <button
                  className={errorStyles.retryButton}
                  onClick={() => window.location.reload()}
                  aria-label="Retry loading the page"
                >
                  Try Again
                </button>
              )}
            </div>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              toastClassName="smart-send-toast"
            />
          </div>
        </>
      );

    case 'NO_DATA':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={errorStyles.errorContainer}>
            <div className={errorStyles.errorCard}>
              <h2 className={errorStyles.errorTitle}>
                No order data available
              </h2>
              <p className={errorStyles.errorMessage}>
                We couldn't find any order information. Please check your link
                and try again.
              </p>
            </div>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              toastClassName="smart-send-toast"
            />
          </div>
        </>
      );

    case 'COMPLETED':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={statusStyles.statusContainer}>
            <div className={statusStyles.statusCard}>
              <div className={statusStyles.successIcon}>
                <span className={statusStyles.checkmark}>✓</span>
              </div>
              <h2 className={statusStyles.title}>Thank you!</h2>
              <p className={statusStyles.message}>
                We have received your delivery details.
              </p>
              {!inApp && (
                <button
                  className={statusStyles.downloadButton}
                  onClick={() => (window.location.href = GESTURE_DOWNLOAD_URL)}
                >
                  Download Gesture
                </button>
              )}
            </div>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              toastClassName="smart-send-toast"
            />
          </div>
        </>
      );

    case 'CANCELLED':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={statusStyles.statusContainer}>
            <div className={statusStyles.statusCard}>
              <h2 className={statusStyles.title}>
                Hi {orderData?.recipientName?.trim() && orderData.recipientName}
              </h2>
              <p className={statusStyles.message}>
                Looks like{' '}
                {orderData?.senderName
                  ? getFirstName(orderData.senderName)
                  : 'Someone'}{' '}
                sent you a gift. There was a change in this order, keep an eye
                out for future notifications.
              </p>
              {!inApp && (
                <button
                  className={statusStyles.downloadButton}
                  onClick={() => (window.location.href = GESTURE_DOWNLOAD_URL)}
                >
                  Download Gesture
                </button>
              )}
            </div>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              toastClassName="smart-send-toast"
            />
          </div>
        </>
      );

    case 'EXPIRED':
      return (
        <>
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}
          <div className={statusStyles.statusContainer}>
            <div className={statusStyles.statusCard}>
              {error?.response?.message?.message &&
              Array.isArray(error.response.message.message) ? (
                error.response.message.message.map((line, idx) =>
                  line.bold ? (
                    <h2 className={statusStyles.title} key={idx}>
                      <strong>{line.text}</strong>
                    </h2>
                  ) : (
                    <p className={statusStyles.message} key={idx}>
                      {line.text}
                    </p>
                  )
                )
              ) : (
                <>
                  <h2 className={statusStyles.title}>
                    <strong>Product Expired</strong>
                  </h2>
                  <p className={statusStyles.message}>
                    This product is no longer available for delivery.
                  </p>
                </>
              )}
              {!inApp && (
                <button
                  className={statusStyles.downloadButton}
                  onClick={() => (window.location.href = GESTURE_DOWNLOAD_URL)}
                >
                  Download Gesture
                </button>
              )}
            </div>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              toastClassName="smart-send-toast"
            />
          </div>
        </>
      );

    case 'MAIN_FORM':
    default:
      return (
        <>
          {/* Hero Video Background */}
          <HeroVideo
            videoUrl={HERO_VIDEO_URL}
            onVideoReady={() => setShowContent(true)}
            contentVisible={showContent}
            skipVideo={skipVideo}
          />

          {/* AppBar for in-app navigation */}
          {inApp && <AppBar submittedOrderId={submittedOrderId} />}

          {/* Smart Send Flow - wrapped in error boundary and context provider */}
          <ErrorBoundary>
            <SmartSendProvider
              orderId={orderId}
              tokenId={tokenId}
              initialCode={code}
              inApp={inApp}
              orderData={orderData}
              secureVerifyHeader={secureVerifyHeader}
            >
              <SmartSendFlow
                showContent={showContent}
                onOrderSubmitted={(id) => setSubmittedOrderId(id)}
                onSubmissionSuccess={(submittedOrderId) => {
                  setIsSubmitted(true);
                  setSubmittedOrderId(submittedOrderId);
                }}
              />
            </SmartSendProvider>
          </ErrorBoundary>

          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            toastClassName="smart-send-toast"
          />
        </>
      );
  }
};

export default SmartSendV2;
